import { ShieldCheck, Search, FlaskConical, Globe } from "lucide-react";
import { useScrollReveal } from "../hooks/useAnimations";
import { assets } from "../lib/assets";

const qcIcons = [Search, ShieldCheck, FlaskConical, Globe];

export default function QualityControl({ t }) {
  const [headerRef, headerVis] = useScrollReveal();
  const [stepsRef, stepsVis] = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="quality" data-testid="quality-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`reveal ${headerVis ? 'visible' : ''}`}>
          <div className="grid lg:grid-cols-12 gap-8 mb-20">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[2px] bg-brand-blue" />
                <span className="text-brand-blue text-xs font-semibold tracking-[0.25em] uppercase">
                  {t.quality.sectionLabel}
                </span>
              </div>
              <h2
                data-testid="quality-title"
                className="font-heading font-bold text-4xl sm:text-5xl text-brand-blue leading-[1.08] tracking-tight mb-5"
              >
                {t.quality.title}
              </h2>
              <p className="text-brand-blue font-medium text-base italic mb-4">
                &ldquo;{t.quality.subtitle}&rdquo;
              </p>
              <p className="text-slate-600 text-base leading-relaxed">
                {t.quality.description}
              </p>
            </div>
            <div className="lg:col-span-7 relative">
              <div className="rounded-2xl overflow-hidden group">
                <img src={assets.qualityLab} alt="Quality lab" className="w-full h-64 lg:h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/90 pointer-events-none" />
              </div>
              <div className="absolute bottom-4 right-4 glass rounded-xl px-4 py-3 shadow-xl animate-float-slow">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-accent-green" />
                  <div>
                    <div className="text-sm font-bold text-emerald-900">ISO & GMP</div>
                    <div className="text-xs text-slate-600">Certified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={stepsRef} className={`relative stagger-children ${stepsVis ? 'visible' : ''}`}>
          <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-px bg-slate-100" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {t.quality.items.map((item, i) => {
              const Icon = qcIcons[i];
              return (
                <div
                  key={i}
                  data-testid={`quality-item-${i}`}
                  className="relative group cursor-default rounded-2xl p-6 border border-brand-blue/20 bg-card-mint"
                >
                  <div className="w-[84px] h-[84px] rounded-full border-2 border-slate-200 group-hover:border-brand-blue bg-white flex items-center justify-center mx-auto mb-6 transition-all duration-500 relative z-10 group-hover:shadow-lg group-hover:shadow-brand-blue/20">
                    <div className="w-16 h-16 rounded-full bg-card-mint group-hover:bg-brand-blue flex items-center justify-center transition-all duration-500">
                      <span className="font-heading font-bold text-2xl text-slate-400 group-hover:text-white transition-colors duration-500">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="w-8 h-8 rounded-lg bg-brand-blue flex items-center justify-center mx-auto mb-3 transition-all duration-400">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-base mb-2 text-[#333333]">{item.title}</h3>
                    <p className="text-sm leading-relaxed max-w-[220px] mx-auto text-slate-600">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
